import { useState, useMemo } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  Globe,
  Users,
  CreditCard,
  Building2,
  Cpu,
  MessageSquare,
  ScrollText,
  Workflow,
  Menu,
  X,
  ChevronRight,
  ShieldCheck,
  Flag,
  ClipboardList,
  Activity,
  BarChart3,
  Key,
  Code2,
} from 'lucide-react';
import { useAgentStore } from '../../stores/agent';
import styles from './admin.module.css';

interface NavItem {
  to: string;
  label: string;
  icon: typeof Globe;
  group: 'core' | 'platform';
}

export function AdminShell() {
  const role = useAgentStore((s) => s.current?.role);
  const isPlatformAdmin = role === 'super-admin';
  const isAccountAdmin = role === 'account-admin';
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const navItems: NavItem[] = useMemo(() => {
    const items: NavItem[] = [
      { to: '/admin/sites', label: 'Sites & Embeds', icon: Globe, group: 'core' },
      { to: '/admin/agents', label: 'Agents & Access', icon: Users, group: 'core' },
      { to: '/admin/usage', label: 'Usage', icon: BarChart3, group: 'core' },
      { to: '/admin/billing', label: 'Plan & Billing', icon: CreditCard, group: 'core' },
    ];

    if (isPlatformAdmin || isAccountAdmin) {
      items.push({ to: '/admin/api-keys', label: 'API Keys', icon: Key, group: 'core' });
    }

    items.push(
      { to: '/admin/developers', label: 'Developers', icon: Code2, group: 'core' },
      { to: '/admin/audit', label: 'Audit Log', icon: ClipboardList, group: 'core' }
    );

    if (isPlatformAdmin) {
      items.push({ to: '/admin/accounts', label: 'Accounts & Tenants', icon: Building2, group: 'core' });
      items.push(
        { to: '/admin/system', label: 'System & AI Engine', icon: Cpu, group: 'platform' },
        { to: '/admin/flags', label: 'Feature Flags', icon: Flag, group: 'platform' },
        { to: '/admin/whatsapp', label: 'WhatsApp Gateway', icon: MessageSquare, group: 'platform' },
        { to: '/admin/logs', label: 'Platform Logs', icon: ScrollText, group: 'platform' },
        { to: '/admin/jobs', label: 'Background Jobs', icon: Workflow, group: 'platform' },
        { to: '/admin/health', label: 'System Health', icon: Activity, group: 'platform' }
      );
    }

    return items;
  }, [isPlatformAdmin, isAccountAdmin]);

  const currentItem = navItems.find((item) => location.pathname.startsWith(item.to)) || navItems[0];

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    isActive ? `${styles.subnavLink} ${styles.subnavLinkActive}` : styles.subnavLink;

  const renderNavGroup = (groupName: 'core' | 'platform', title: string) => {
    const groupItems = navItems.filter((i) => i.group === groupName);
    if (groupItems.length === 0) return null;

    return (
      <div className={styles.navGroup} key={groupName}>
        <div className={styles.navGroupLabel}>{title}</div>
        {groupItems.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              className={linkClass}
              onClick={() => setDrawerOpen(false)}
            >
              <Icon size={16} aria-hidden />
              <span>{item.label}</span>
            </NavLink>
          );
        })}
      </div>
    );
  };

  return (
    <section className={styles.adminRoot}>
      {/* Mobile Top Context Bar */}
      <div className={styles.mobileAdminBar}>
        <div className={styles.mobileCurrentSection}>
          {currentItem && <currentItem.icon size={16} color="var(--inic-accent)" />}
          <span>{currentItem?.label ?? 'Admin'}</span>
        </div>
        <button
          type="button"
          className={styles.mobileMenuBtn}
          onClick={() => setDrawerOpen(true)}
          aria-label="Open Admin Menu"
        >
          <Menu size={16} />
          <span>Menu</span>
        </button>
      </div>

      {/* Mobile Drawer */}
      {drawerOpen && (
        <div className={styles.mobileDrawerOverlay} onClick={() => setDrawerOpen(false)}>
          <div className={styles.mobileDrawer} onClick={(e) => e.stopPropagation()}>
            <div className={styles.mobileDrawerHead}>
              <div className={styles.sidebarTitle}>Admin Console</div>
              <button
                type="button"
                className={styles.drawerCloseBtn}
                onClick={() => setDrawerOpen(false)}
                aria-label="Close Admin Menu"
              >
                <X size={18} />
              </button>
            </div>
            <div className={styles.sidebarRoleBadge}>
              <span className={styles.sidebarRoleDot} />
              {isPlatformAdmin ? 'Super Admin' : 'Account Admin'}
            </div>
            {renderNavGroup('core', 'Workspace Scope')}
            {isPlatformAdmin && renderNavGroup('platform', 'Platform Engine')}
          </div>
        </div>
      )}

      {/* Desktop Navigation Sidebar */}
      <aside className={styles.adminSidebar} aria-label="Admin Navigation">
        <div className={styles.sidebarHeader}>
          <div className={styles.sidebarTitle}>Admin Console</div>
          <div className={styles.sidebarRoleBadge}>
            <span className={styles.sidebarRoleDot} />
            {isPlatformAdmin ? 'Platform Operator' : 'Account Admin'}
          </div>
        </div>

        {renderNavGroup('core', 'Workspace Scope')}
        {isPlatformAdmin && renderNavGroup('platform', 'Platform Engine')}

        <div className={styles.sidebarFooter}>
          <div className={styles.sidebarMetaText}>
            <ShieldCheck size={12} style={{ display: 'inline', verticalAlign: '-1px', marginRight: 4 }} />
            Openline Sovereign v2.4
          </div>
        </div>
      </aside>

      {/* Main Administrative Body */}
      <div className={styles.adminBody}>
        {/* Breadcrumb Path */}
        <nav className={styles.adminBreadcrumb} aria-label="Breadcrumb">
          <span>Admin</span>
          <ChevronRight size={12} className={styles.adminBreadcrumbSep} />
          <span className={styles.adminBreadcrumbCurrent}>{currentItem?.label ?? 'Dashboard'}</span>
        </nav>

        {/* Content Outlet */}
        <div className={styles.adminContentWrapper}>
          <Outlet />
        </div>
      </div>
    </section>
  );
}
